import { memo, type ReactNode } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  insightsSubsectionAccordionDetailsSx,
  insightsSubsectionAccordionSummarySx,
  insightsSubsectionAccordionSx,
  insightsSubsectionFlatHeaderSx,
  insightsSubsectionFlatPanelSx,
  insightsSubsectionIconBoxSx,
} from "./insightsSubsectionStyles";

export interface InsightsSubsectionPanelProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Estado inicial do accordion (padrão: expandido). */
  defaultExpanded?: boolean;
  /** Identificador para acessibilidade (aria-controls / id). */
  panelId?: string;
  /** "flat" renderiza título + conteúdo sem acordeão. */
  variant?: "accordion" | "flat";
}

function SubsectionHeader({
  icon,
  title,
  subtitle,
}: Pick<InsightsSubsectionPanelProps, "icon" | "title" | "subtitle">) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: "100%" }}>
      <Paper variant="outlined" sx={insightsSubsectionIconBoxSx}>
        {icon}
      </Paper>
      <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

/**
 * Subseção de Insights — acordeão colapsável ou painel flat (sempre visível).
 */
const InsightsSubsectionPanel = memo(
  ({
    icon,
    title,
    subtitle,
    children,
    defaultExpanded = true,
    panelId = "insights-subsection",
    variant = "accordion",
  }: InsightsSubsectionPanelProps) => {
    const headerId = `${panelId}-header`;
    const contentId = `${panelId}-content`;

    if (variant === "flat") {
      return (
        <Paper elevation={0} sx={insightsSubsectionFlatPanelSx}>
          <Box id={headerId} sx={insightsSubsectionFlatHeaderSx}>
            <SubsectionHeader icon={icon} title={title} subtitle={subtitle} />
          </Box>
          <Box id={contentId} sx={insightsSubsectionAccordionDetailsSx}>
            {children}
          </Box>
        </Paper>
      );
    }

    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        sx={insightsSubsectionAccordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={contentId}
          id={headerId}
          sx={insightsSubsectionAccordionSummarySx}
        >
          <SubsectionHeader icon={icon} title={title} subtitle={subtitle} />
        </AccordionSummary>

        <AccordionDetails id={contentId} sx={insightsSubsectionAccordionDetailsSx}>
          {children}
        </AccordionDetails>
      </Accordion>
    );
  },
);

InsightsSubsectionPanel.displayName = "InsightsSubsectionPanel";

export default InsightsSubsectionPanel;
